import { Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton, Link, Paper, Snackbar, SnackbarCloseReason, Typography } from "@mui/material"
import { Fragment, useState } from "react";
import { RiskScoreAnalysis, OpenAlexReferenceData, BootstrapDialog } from "./CommonInterfaces";
import RiskScorePanel from "./RiskScorePanel";
import CloseIcon from '@mui/icons-material/Close';
import ArticleTitle from "./ArticleTitle";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";

interface DoiAnalysisLinkProps {
    doi: string,
    newRetractions: string[],
    showDoiInTitle: boolean,
    whenLoading?: () => void,
    whenLoaded?: () => void
}


function DoiAnalysisLink({ doi, newRetractions, showDoiInTitle, whenLoading = () => { }, whenLoaded = () => { } }: DoiAnalysisLinkProps) {
    const [doiSearchResult, setDoiSearchResult] = useState<RiskScoreAnalysis>();
    const [problematicReferences, setProblematicReferences] = useState<OpenAlexReferenceData[]>([])
    const [open, setOpen] = useState(false);
    const [openError, setOpenError] = useState(false);

    const handleCloseError = (
        event: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenError(false);
    };


    const handleClose = () => {
        setOpen(false);
    };

    async function fetchDataFromAPI(url: string): Promise<OpenAlexReferenceData> {
        let authToken = await auth.currentUser?.getIdToken(true)

        return fetch(url, {
            method: 'GET',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
                'Authorization': 'Bearer ' + authToken
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            });
    }


    const showAnalysis = async (doi: string) => {
        let authToken = await auth.currentUser?.getIdToken(true)
        const response = await fetch(argosApiEntryPoint + '/risk-score?doi=' + doi, {
            method: 'GET',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
                'Authorization': 'Bearer ' + authToken
            }
        });
        if (response.ok) {

            const riskScoreAnalysis: RiskScoreAnalysis = await response.json();

            const problematicStuff = riskScoreAnalysis.referencesAnalysis.filter(ref => ref.problemsFound)

            // TODO: Remove runtime dependency from OpenAlex
            setProblematicReferences([])

            if (problematicStuff.length === 0) {
                setDoiSearchResult(riskScoreAnalysis)

            } else {
                problematicStuff.map(async (p, i) => {
                    setTimeout(function () {
                        fetchDataFromAPI(
                            argosApiEntryPoint + '/tmp/references?id=' + p.referenceOpenAlexId
                        ).then(results => {
                            setProblematicReferences((prev) => [...prev, results])
                            if (i === (problematicStuff.length - 1)) {
                                setDoiSearchResult(riskScoreAnalysis)
                                setOpen(true)
                            }
                        }).catch(err => {
                            setOpenError(true)
                        }
                        );
                    }, 250 * i);
                })
            }

        }
    }

    return <><Link href="#" onClick={(e) => {
        e.preventDefault()
        setOpen(true);
        showAnalysis(doi);
    }}>{showDoiInTitle && <Typography variant="body2" sx={{fontSize: 10}}>{doi}</Typography>}
        {!showDoiInTitle && <Typography>See our analysis</Typography>}
    </Link>

        <Fragment>
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                maxWidth="xl"
            >

                <DialogTitle sx={{ m: 0, pl: 2, pr: 4, fontSize: { sm: '1em', md: '2em', xl: '3em' } }}
                    id="customized-dialog-title">
                    {doiSearchResult &&
                        <ArticleTitle title={doiSearchResult.title} />
                    }
                    {!doiSearchResult &&
                        <ArticleTitle title="Loading analysis..." />
                    }
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    {!doiSearchResult &&
                        <Box sx={{ justifyContent: 'center', textAlign: 'center' }}>
                            <CircularProgress />
                        </Box>}
                    {doiSearchResult &&
                        <RiskScorePanel data={doiSearchResult} problematicReferences={problematicReferences} newRetractions={newRetractions} />
                    }
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleClose}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
            <Snackbar
                open={openError}
                autoHideDuration={5000}
                onClose={handleCloseError}
                message="Sorry, we are unable to retrieve all the data for this analysis. Partial information might be shown."
            />
        </Fragment>

    </>
}

export default DoiAnalysisLink